/**
 * Ledger aggregation (ARCHITECTURE §3.1). Posts entries into per-account
 * balances. Line amounts are already in base currency (see types.ts), so this
 * is exact integer accumulation — no conversion, no rounding.
 *
 * Only `posted` entries affect balances; drafts are excluded.
 */
import type { Account, Currency, JournalEntry, Money } from "./types";
import { add, subtract, zero } from "./money";
import { isDebitNormal } from "./chartOfAccounts";

/** Net debit and credit accumulated on one account. */
export interface AccountBalance {
  readonly code: string;
  readonly debit: Money;
  readonly credit: Money;
  /**
   * Signed balance in the account's natural direction: positive means the
   * account carries its normal balance (debit-normal → net debit positive).
   */
  readonly balance: Money;
}

/**
 * Aggregate posted entries into account balances. Pass `entityId` to restrict
 * to one entity; omit for the whole group (raw consolidation).
 */
export function buildLedger(
  entries: readonly JournalEntry[],
  accounts: readonly Account[],
  base: Currency,
  entityId?: string,
): Map<string, AccountBalance> {
  const debit = new Map<string, Money>();
  const credit = new Map<string, Money>();

  const relevant = entries.filter(
    (e) => e.status === "posted" && (entityId === undefined || e.entityId === entityId),
  );

  for (const entry of relevant) {
    for (const line of entry.lines) {
      debit.set(
        line.accountCode,
        add(debit.get(line.accountCode) ?? zero(base), line.debit),
      );
      credit.set(
        line.accountCode,
        add(credit.get(line.accountCode) ?? zero(base), line.credit),
      );
    }
  }

  const typeByCode = new Map(accounts.map((a) => [a.code, a.type]));
  const result = new Map<string, AccountBalance>();
  const codes = new Set<string>([...debit.keys(), ...credit.keys()]);

  for (const code of codes) {
    const dr = debit.get(code) ?? zero(base);
    const cr = credit.get(code) ?? zero(base);
    const type = typeByCode.get(code);
    const net = subtract(dr, cr); // positive = net debit
    const balance = type && !isDebitNormal(type) ? subtract(cr, dr) : net;
    result.set(code, { code, debit: dr, credit: cr, balance });
  }

  return result;
}

/** Look up a single account's signed natural balance (zero if unposted). */
export function accountBalance(
  ledger: ReadonlyMap<string, AccountBalance>,
  code: string,
  base: Currency,
): Money {
  return ledger.get(code)?.balance ?? zero(base);
}

/** Sum the natural balances of every account of the given types. */
export function sumByTypes(
  ledger: ReadonlyMap<string, AccountBalance>,
  accounts: readonly Account[],
  types: readonly Account["type"][],
  base: Currency,
): Money {
  const typeByCode = new Map(accounts.map((a) => [a.code, a.type]));
  let total = zero(base);
  for (const bal of ledger.values()) {
    const type = typeByCode.get(bal.code);
    if (type && types.includes(type)) total = add(total, bal.balance);
  }
  return total;
}
